// ============================================================
// ERROR BOUNDARY — 画面描画中の未捕捉エラーで白画面のまま固まるのを防ぐ。
//   - main.tsx（root 直下）と App.tsx（AppShell の <Suspense> の外側）に1枚ずつ。
//   - lazyRetry.ts の「リロード後も失敗する chunk エラー」も最終的にここで受ける。
//   - fallback は既存トーン（bg-white / border-hair / rounded-card）＋「再読み込み」。
//   - エラーは console.error に記録する（トークン・email 等の秘密は出さない：
//     React が投げた Error オブジェクトと componentStack のみ）。
// ============================================================
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 調査用の記録のみ（Error 本体と component stack。秘密・個人情報は含めない）。
    console.error('[ErrorBoundary] 画面の描画中にエラーが発生しました', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[50vh] grid place-items-center p-6">
          <div className="w-full max-w-md bg-white border border-hair rounded-card p-8 text-center">
            <p className="text-base font-bold text-charcoal">問題が発生しました</p>
            <p className="mt-2 text-sm text-slate-sub">
              一時的なエラーの可能性があります。再読み込みをお試しください。
              解消しない場合は、たいきに連絡してください。
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-5 inline-flex items-center rounded-soft border border-hair bg-white px-4 py-2 text-sm font-semibold text-charcoal hover:bg-base"
            >
              再読み込み
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
